import Vue from 'vue';
import {Prop, Watch} from 'vue-property-decorator';
import Component, {mixins} from 'vue-class-component';
import {mapGetters} from 'vuex';
import FormWizard from 'vue-form-wizard/src/components/FormWizard.vue';
import TabContent from 'vue-form-wizard/src/components/TabContent.vue';
import WizardButton from 'vue-form-wizard/src/components/WizardButton.vue';
import {
  CLEAR_BUYER_DATA,
  CLEAR_IS_GUEST_ORDER,
  CLEAR_REGISTRATION_CODES,
  CLEAR_SESSION,
  CLEAR_USER,
  SET_BUYER_DATA,
  UPDATE_ORDER,
} from '../../../store/commits';
import CustomerForm from '../CustomerForm/CustomerForm.vue';
import VisitorSurveyComponent from '../VisitorSurveyComponent/VisitorSurveyComponent.vue';
import PaymentType from '../PaymentType/PaymentType.vue';
import CustomerData from '../CustomerData/CustomerData.vue';
import SepaForm from '../PaymentInfoForms/SepaForm/SepaForm.vue';
import CreditCardForm from '../PaymentInfoForms/CreditCardForm/CreditCardForm.vue';
import CartView from '../../Cart/CartView/CartView.vue';
import {Client, Country, Event, SubmitOrderResponse, PrivacyStatement, State, TermsAndConditions, Title, VisitorSegment} from '../../../Models';
import {loadScript} from '@paypal/paypal-js';
import {
  ApplePayDTO,
  CheckoutDataDTO,
  CreditCardDTO,
  CustomerDataDTO,
  PaymentInfoDTO,
  PayPalDTO,
  SepaDTO,
  SessionDTO,
  ShopUrlsDTO,
  VisitorSurveyResultDTO,
} from '../../../DTOs';
import {
  ApplePayService,
  AuthService,
  ConcardisService,
  PayPalService,
  PrivacyStatementService,
  TermsAndConditionsService,
  VisitorSegmentService,
} from '../../../Services';
import {VuexObjectMixin} from '../../../Mixins';
import {
  AddressTypeEnum,
  OrderCheckoutStepEnum,
  PaymentInfoFormEnum,
  PaymentTypeEnum,
  ProductQuantityErrorEnum,
  PublicLinks,
  VisitorSegmentEnum,
} from '../../../Enums';
import PersonalUseModal from '../../Components/PersonalUseModal/PersonalUseModal.vue';
import OrderPublicTransportModal from '../../Components/OrderPublicTransportModal/OrderPublicTransportModal.vue';
import Vvs from '../../Icons/Vvs.vue';
import PublicTransportTicket from '../../Icons/PublicTransportTicket.vue';
import {ApiErrorHandler} from '../../../Errors/ApiErrorHandler';
import Modal from '../../Modal/Modal.vue';
import {OrderErrorCodes} from '../../../Errors/OrderErrorCodes';
import {AdditionalNoticeService} from '../../../Services/AdditionalNoticeService';
import {AdditionalNotice} from '../../../Models/AdditionalNotice';
import {AdditionalOrderItemConfiguration} from '../../../Models/AdditionalOrderItemConfiguration';
import {AdditionalOrderItemConfigurationDTO} from '../../../DTOs/AdditionalOrderItemConfigurationDTO';
import {LocalStorageUtility} from '../../../Utilities';
import {DEFAULT_LANGUAGE} from '../../../constants';

@Component({
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      initialBuyerData: 'getBuyerData',
      sessionDTO: 'getSession',
      isGuestOrder: 'getIsGuestOrder',
      countries: 'getCountries',
      states: 'getStates',
      titles: 'getTitles',
    }),
  },
  components: {
    Modal,
    FormWizard,
    TabContent,
    WizardButton,
    CustomerForm,
    CustomerData,
    CartView,
    VisitorSurveyComponent,
    PaymentType,
    PersonalUseModal,
    OrderPublicTransportModal,
    PublicTransportTicket,
    Vvs,
    SepaForm,
    CreditCardForm,
  },
})
export default class CheckoutView extends mixins(VuexObjectMixin) {
  @Prop({type: String, required: true}) successUrl: string | undefined;
  @Prop({type: String, required: true}) failureUrl: string | undefined;
  @Prop({type: String, required: true}) cancelUrl: string | undefined;
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({type: String, required: true}) loginUrl: string | undefined;
  @Prop({type: String, required: true}) loginPageUrl: string | undefined;
  @Prop({type: String, required: true}) ticketShopUrl: string | undefined;

  private initialBuyerData!: CustomerDataDTO | null;
  private isGuestOrder!: boolean | null;
  private countries!: Array<Country>;
  private states!: { [key: string]: Array<State> };
  private titles!: Array<Title>;
  private payPalUcIdentifier = 'JiFH1ubU';

  // instance data

  // currentIndex is controlled by form wizard, it may not update as fast as we need it for some operations
  private currentIndex: number = 0;

  // that's why we have a programmaticIndex, which gets synced by currentIndex,
  // but updated more immediately when we need it
  private programmaticIndex: number = 0;
  private paymentProducts: Array<string> = [];
  private visitorSegments: Array<VisitorSegment> = [];
  private showPaymentErrors: boolean = false;
  private showPrivacyErrors: boolean = false;
  private showQuantityError: boolean = false;
  private missingDataProtection: boolean = false;
  private quantityErrorData: any | null = null;

  private personalizationValid: boolean = true;
  private pubTransValid: boolean = true;
  private paymentValid: boolean = true;
  private personalizationFormValid: boolean = true;
  private paymentFormValid: boolean = true;
  private checkoutValid: boolean = true;

  private inactivityTimer;
  private isInactive: boolean = false;

  private hideSurvey: boolean = false;
  private loading: boolean = false;
  private validateSurvey: boolean = false;

  // dtos
  private personalizationDTO = new CustomerDataDTO();
  private invoiceDataDTO = new CustomerDataDTO();
  private paymentInfoDTO: PaymentInfoDTO | null = null;
  private checkoutDataDTO = new CheckoutDataDTO();
  private shopUrlsDTO = new ShopUrlsDTO();
  private sessionDTO: SessionDTO | null | undefined;

  // services
  private privacyStatementService!: PrivacyStatementService;
  private termsAndConditionsService!: TermsAndConditionsService;
  private additionalNoticeService!: AdditionalNoticeService;
  private visitorSegmentService!: VisitorSegmentService;
  private concardisService!: ConcardisService;
  private authService!: AuthService;
  private applePayService!: ApplePayService;
  private applePaySession: ApplePaySession | null = null;
  private payPalService!: PayPalService;

  private currentClient: string | null = null;
  private privacyStatements: any = {};
  private agb: any = {};
  private additionalNotices: Array<AdditionalNotice> = [];

  private isMounted: boolean = false;
  private isSubmitting: boolean = false;
  private submittedPaymentForm: boolean = false;
  private paypalOrderId: string | null = null;
  private isAwaitingNextTab: boolean = false;
  private goToStep: number | null = null;
  private hasSentDataLayer: boolean = false;
  private pageRefreshed: boolean = false;

  async runBefore() {
    this.privacyStatementService = new PrivacyStatementService(this.inventoryUrl);
    this.termsAndConditionsService = new TermsAndConditionsService(this.inventoryUrl);
    this.additionalNoticeService = new AdditionalNoticeService(this.inventoryUrl);
    this.visitorSegmentService = new VisitorSegmentService(this.inventoryUrl);
    this.authService = new AuthService(this.loginUrl);
    this.concardisService = new ConcardisService(this.inventoryUrl);
    this.applePayService = new ApplePayService(this.inventoryUrl);
    this.payPalService = new PayPalService(this.inventoryUrl);
    if (this.applePayService.isApplePayAvailable()) {
      this.applePayService.setMerchantIdentifier(await this.applePayService.fetchMerchantIdentifier());
    }
    if (this.currentOrder) {
      this.checkoutDataDTO = this.checkoutDataDTO.fromModel(this.currentOrder);
      this.invoiceDataDTO = (new CustomerDataDTO()).fromData(this.checkoutDataDTO.invoiceData);
      this.shopUrlsDTO = (new ShopUrlsDTO()).fromData(this.checkoutDataDTO.shopUrls);
      if (this.initialBuyerData) {
        this.personalizationDTO = (new CustomerDataDTO()).fromData(this.initialBuyerData);
      }
      this.checkoutDataDTO.buyerData = (new CustomerDataDTO()).fromData(this.personalizationDTO);

      if (this.personalizationDTO.paymentOption) {
        this.initPaymentInfoDTO(this.personalizationDTO.paymentOption);
      }
    }

    // not logged in/as guest
    if (!this.initialBuyerData && this.loginPageUrl) {
      // redirect to login intermediate step
      window.location.href = this.loginPageUrl;
      return;
    }

    window.addEventListener('ucEvent', this.onPayPalConsentChange);
  }

  async mounted() {
    await this.runBefore();
    this.$store.commit(CLEAR_REGISTRATION_CODES);
    this.hasSentDataLayer = sessionStorage.getItem('sentDataLayerPayment') === '1';
    if (this.hasSentDataLayer) {
      this.pageRefreshed = true;
    }
    if (this.currentOrder) {
      await this.fetchData();
      // @ts-ignore
      document.querySelector('.bodyWrapper').classList.add('bodyWrapper--smallContent');
      this.initFormWizardListeners();
      this.isMounted = true;
      if ((window.location.search && window.location.search.length > 0) || !this.hasItemsWithRegistrationRequired) {
        let goToStep = this.getStepFromUrl();

        if (!goToStep) {
          goToStep = this.determineSecondStep;
        }

        if (goToStep) {
          this.loading = true;
          await this.toStep(
            (isNaN(goToStep)
              || goToStep < OrderCheckoutStepEnum.PERSONALIZATION
              || goToStep > OrderCheckoutStepEnum.REVIEW) ? OrderCheckoutStepEnum.PERSONALIZATION : goToStep);
          return;
        }
      }
      this.showPersonalisationModal();
    } else {
      this.initFormWizardListeners();
      this.isMounted = true;
    }
    this.isMounted = true;
  }

  beforeDestroy() {
    window.removeEventListener('ucEvent', this.onPayPalConsentChange);
  }

  async onPayPalConsentChange(e: any) {
    // uc v1
    if (e.data && e.data.event && e.data.event === 'consent_changed' && 'PayPal' in e.data) {
      if (e.data.PayPal === true) {
        await this.enablePayPal();
      } else {
        this.disablePayPal();
      }
    }
    // uc v2
    if (e.detail && e.detail.event === 'consent_status' && e.detail.action === 'onUpdateServices' && 'PayPal' in e.detail) {
      if (e.detail.PayPal === true) {
        await this.enablePayPal();
      } else {
        this.disablePayPal();
      }
    }
  }

  async enablePayPal() {
    // @ts-ignore
    if (this.$refs.consentModal) {
      // @ts-ignore
      this.$refs.consentModal.closeModal();
      this.missingDataProtection = false;
      await this.fetchPayPalSdkJs();
    }
  }

  disablePayPal() {
    // @ts-ignore
    if (this.$refs.consentModal) {
      // @ts-ignore
      this.$refs.consentModal.showModal();
      this.deletePayPalFromDOM();
      this.missingDataProtection = true;
    }
  }

  async fetchData(): Promise<void> {
    this.isRequesting = true;
    try {
      this.setAvailablePaymentMethods();
      await Promise.all([
        this.fetchVisitorSegments(),
        this.$store.dispatch('fetchMiscData', this.inventoryUrl),
      ]);
    } catch (e) {
      console.error(e);
      // TODO: display error
    } finally {
      this.isRequesting = false;
    }
  }

  async fetchVisitorSegments() {
    try {
      this.visitorSegments = await this.visitorSegmentService.listCached<VisitorSegment>();
    } catch (e) {
      console.error(e);
      this.visitorSegments = [];
    }
  }

  async fetchPayPalSdkJs() {
    this.missingDataProtection = false;

    let allowedPayPal = false;
    if (window.usercentrics) {
      const consents = window.usercentrics.getConsents();
      const ppProvider = consents.find((p: string) => p === this.payPalUcIdentifier);
      allowedPayPal = ppProvider && ppProvider.consentStatus === true;
    } else if (window.uc && window.uc.ucapi && window.uc.ucapi.getWhitelistedServices) {
      const consents = window.uc.ucapi.getWhitelistedServices();
      allowedPayPal = consents.find((p: string) => p === this.payPalUcIdentifier) !== undefined;
    } else {
      console.error('Could not get information about UC consents.');
      return;
    }

    // @ts-ignore
    if (allowedPayPal) {
      if (!window.paypal_sdk || !window.paypal_sdk.FUNDING) {
        if (window.paypal_sdk) {
          // needs complete removal as paypal will check only for its existence
          delete window.paypal_sdk;
        }
        try {
          const response = await this.payPalService.getClientId();
          await loadScript(
            {
              'client-id': response.clientId,
              'currency': 'EUR',
              'data-namespace': 'paypal_sdk',
            },
          );
        } catch (error) {
          console.error('failed to load the PayPal JS SDK script', error);
        }
      }

      if (window.paypal_sdk && window.paypal_sdk.FUNDING) {
        try {
          const paypalService = this.payPalService;
          const that = this;
          await window.paypal_sdk
            .Buttons({
              // Sets up the transaction when a payment button is clicked
              createOrder: function (data, actions) {
                return paypalService.createPayPalOrder(that.currentOrder)
                  .then((response) => response)
                  .then((order) => {
                    that.paypalOrderId = order.id;
                    return order.id;
                  });
              },
              onApprove: async function (data, actions) {
                await that.processCheckout();
              },
              style: {
                label: 'buynow',
              },
              // Only render "PayPal"-button
              fundingSource: window.paypal_sdk.FUNDING.PAYPAL,
            }).render('#paypal-buttons-container');
        } catch (error) {
          console.error('failed to render the PayPal Buttons', error);
        }
      } else {
        console.error('PayPal sdk was not loaded!');
      }
    } else {
      // @ts-ignore
      this.$refs.consentModal.showModal();
      this.missingDataProtection = true;
    }
  }

  showUcPayPalInformation(): void {
    if (window.uc) {
      window.uc.ucapi.showInfoModal(this.payPalUcIdentifier);
    }
  }

  closeUcPayPalInformation(): void {
    // @ts-ignore
    this.$refs.consentModal.closeModal();
    this.toStep(OrderCheckoutStepEnum.PAYMENT);
  }

  deletePayPalFromDOM() {
    if ('paypal_sdk' in window) {
      if (window.paypal_sdk.Buttons && window.paypal_sdk.Buttons.close) {
        window.paypal_sdk.Buttons.close();
      }
    }

    if (this.$refs.paypalButtonContainer) {
      // @ts-ignore
      this.$refs.paypalButtonContainer.childNodes.forEach(cn => cn.remove());
    }
  }

  async acceptPayPalConsent(): Promise<void> {
    if (window.uc) {
      window.uc.ucapi.setConsents([{'templateId': this.payPalUcIdentifier, 'status': true}]);
      // @ts-ignore
      this.$refs.consentModal.closeModal();
    }
  }

  updateCurrentIndex(prevIndex: number, nextIndex: number) {
    this.currentIndex = nextIndex >= 0 ? nextIndex : 0;
    if (!this.isAwaitingNextTab && !this.goToStep) {
      this.programmaticIndex = this.currentIndex;
    }
    this.updateWindowUrl();
  }

  updatePersonalizationDTO(updateData: { dto: CustomerDataDTO, missingRequiredFields: Array<string> }) {
    Object.keys(updateData.dto).forEach((k) => {
      Vue.set(this.personalizationDTO, k, updateData.dto[k]);
    });
    this.personalizationFormValid = updateData.missingRequiredFields.length === 0;
  }

  updatePaymentDataDTO(updateData: { dto: CustomerDataDTO, missingRequiredFields: Array<string> }) {
    this.isMounted = !this.isMounted;
    this.isMounted = true;
    Object.keys(updateData.dto).forEach((k) => {
      Vue.set(this.invoiceDataDTO, k, updateData.dto[k]);
    });
    this.paymentFormValid = updateData.missingRequiredFields.length === 0;
  }

  updateAdditionalOrderItemConfig(additionalOrderItemConfig: AdditionalOrderItemConfiguration) {
    this.cart?.order.additionalOrderItemConfigurations.forEach((aoic, index) => {
      if (this.cart?.order) {
        // Update config
        if (aoic.id === additionalOrderItemConfig.id) {
          this.cart.order.additionalOrderItemConfigurations[index] = additionalOrderItemConfig;
        }
        // Also update all child-configs (date and usePubTrans if parent was set to false) if there are any
        if (aoic.parentOrderItemId === additionalOrderItemConfig.orderItemId) {
          // @ts-ignore
          this.cart.order.additionalOrderItemConfigurations[index].publicTransportConfig.selectedDate
            // @ts-ignore
            = additionalOrderItemConfig.publicTransportConfig.selectedDate;
          if (additionalOrderItemConfig.publicTransportConfig && !additionalOrderItemConfig.publicTransportConfig.usePubTrans) {
            // @ts-ignore
            this.cart.order.additionalOrderItemConfigurations[index].publicTransportConfig.usePubTrans
              = additionalOrderItemConfig.publicTransportConfig.usePubTrans;
          }
        }
      }
    });
  }

  showPersonalisationModal(): void {
    if (this.$refs.personalUseModal) {
      // @ts-ignore
      this.$refs.personalUseModal.showModal();
    }
  }

  async processCheckout(): Promise<boolean> {
    this.showPrivacyErrors = true;
    if (!this.currentOrder) {
      return false;
    }

    this.isSubmitting = true;
    this.checkoutValid = this.validatePaymentForm()
      && this.validateSurveys()
      && this.validatePersonalizationForm()
      && this.acceptedClientsAGB
      && this.acceptedClientsPrivacyStatements;
    if (!this.checkoutValid) {
      this.isSubmitting = false;
      return this.checkoutValid;
    }

    // Check if paypal-payment was approved by customer, if paypal was selected
    if (this.currentOrder.buyerData &&
      this.currentOrder.buyerData.paymentOption === PaymentTypeEnum.paypal.value &&
      !this.paypalOrderId) {
      return false;
    }

    // unset paypalOrderId for cases someone selects another payment-method after paypal was selected
    if (this.currentOrder.buyerData && this.currentOrder.buyerData.paymentOption !== PaymentTypeEnum.paypal.value) {
      this.paypalOrderId = null;
    }

    // Always set additionalNotices to buyerData, 'cause there's no checkbox for it
    if (this.currentOrder.buyerData instanceof CustomerDataDTO) {
      this.personalizationDTO.additionalNotices = this.additionalNotices ?? [];
    }

    try {
      const success = await this.updateCheckoutData();
      if (!success) {
        return false;
      }
      const result: SubmitOrderResponse = await this.orderService.finishOrder(
        {
          orderId: this.currentOrder!.id,
          token: this.currentOrder!.token,
          applePayToken: this.personalizationDTO.paymentOption === PaymentTypeEnum.applepay.value ?
            (this.paymentInfoDTO as ApplePayDTO).applePayToken :
            null,
          paypalOrderId: this.paypalOrderId,
        },
        this.userLanguage,
      );
      LocalStorageUtility.set<SubmitOrderResponse>('submittedOrder', result);
      this.postCheckoutSuccess(result);
      return true;
    } catch (e) {
      // TODO: sentry error
      this.handleApiError(e);
      this.isSubmitting = false;
      return false;
    }
  }

  async validatePersonalization() {
    this.personalizationValid = this.validatePersonalizationForm() && this.validateSurveys(true);

    if (this.personalizationValid) {
      // check if pubtrans was set and is invalid, to unset it first
      if (!this.isPublicTransportValid()) {
        this.unsetPubTransConfigs();
      }

      this.personalizationValid = await this.updateCheckoutData();
    }
    if (this.$refs.personalUseModal) {
      // @ts-ignore
      this.$refs.personalUseModal.closeModal();
    }
    return this.personalizationValid;
  }

  validatePersonalizationForm(): boolean {
    if (!this.hasSelectedForPersonalUse) {
      return true;
    }

    return this.personalizationFormValid;
  }

  async validatePubTrans(): Promise<boolean> {
    this.isSubmitting = true;
    if (!this.anyProductHasPubTransIncluded) {
      this.isSubmitting = false;
      return true;
    }
    this.pubTransValid = this.isPublicTransportValid();
    if (this.pubTransValid) {
      await this.submitPublicTransport();
    }
    this.isSubmitting = false;
    return this.pubTransValid;
  }

  postCheckoutSuccess(result: SubmitOrderResponse | null = null) {
    const orderNumber = this.currentOrder!.orderNumberCompiled;
    window.addEventListener('pagehide', async (event) => {

      if (this.isGuestOrder) {
        this.$store.commit(CLEAR_USER);
        this.$store.commit(CLEAR_BUYER_DATA);
        this.$store.commit(CLEAR_IS_GUEST_ORDER);
      } else {
        await this.logout();
      }
    });
    if (result && result.redirectUrl) {
      window.location.href = result.redirectUrl;
    } else {
      window.location.href = `${this.successUrl}#${orderNumber}`;
    }
  }

  /**
   * Call the validate function on each of the visitor survey components.
   * As we are defining a vue ref in a for loop, this.$refs[<surveyRefName>] is an array with only a single item.
   */
  validateSurveys(showErrors = false): boolean {
    let valid = true;
    this.events.forEach(e => {
      const componentRefName = `survey${e.id}`;
      // @ts-ignore
      if (this.$refs[componentRefName] && this.$refs[componentRefName].length > 0) {
        // @ts-ignore
        valid = valid && this.$refs[componentRefName][0].validate(showErrors);
      }
    });
    return valid;
  }

  async validatePayment(): Promise<boolean> {
    // submittedPaymentForm => if true, errors can be shown (important for payment methods)
    this.submittedPaymentForm = true;
    this.isSubmitting = true;
    this.paymentValid = this.validatePaymentForm();
    if (this.paymentValid) {
      this.paymentValid = await this.updateCheckoutData();
      this.showPaymentErrors = !this.paymentValid;
    } else {
      this.showPaymentErrors = true;
    }
    this.isSubmitting = false;
    return this.paymentValid;
  }

  validatePaymentForm(): boolean {
    let precond = true;

    if (this.personalizationDTO.useInvoiceAddress) {
      // @ts-ignore
      precond = this.paymentFormValid;
    }
    return precond
      && (
        !this.cart
        || !this.paymentProducts.length
        || (this.cart && !(this.cart.sum > 0))
        || (
          this.cart
          && this.cart.sum > 0
          && (this.paymentInfoDTO === null || (this.paymentInfoDTO !== null && this.paymentInfoDTO.validate()))
          && this.personalizationDTO.paymentOption !== null
          && this.personalizationDTO.paymentOption.length >= 0
        )
      );
  }

  hasSurvey(event: Event): boolean {
    return event.visitorSurvey !== null;
  }

  initFormWizardListeners(): void {
    if (this.$refs.formWizard) {
      // @ts-ignore
      this.$refs.formWizard.$on('on-validate', async (valid: boolean, activeTabIndex: number) => {
        if (this.isAwaitingNextTab && this.goToStep) {
          this.isAwaitingNextTab = false;
          if (this.programmaticIndex <= this.goToStep) {
            this.programmaticIndex++;
            await this.toStep(this.goToStep);
          }
        }
      });

      // @ts-ignore
      this.$refs.formWizard.$on('on-loading', (loading: boolean) => {
        this.loading = loading;
      });
    }
  }

  /**
   *
   * This probably needs explanation
   *
   * We have 3 ways of controlling the different tabs in the checkout process
   * 1. The user clicks on the "continue" buttons
   * 2. The user clicks explicitly on a tab
   * 3. We have a parameter in the url / we need to skip one or multiple steps because requirements aren't met
   *
   * The formWizard-intern tab counter (and our currentIndex) does not have the correct index of a tab
   * when we need to determine it, that's why we have a programmaticIndex for skipping multiple steps
   * which gets counted upwards in this function and always keeps track of the current tab index
   *
   * A listener on the form listener then determines whether the validations were successful and jumps multiple steps
   *
   * @param stepIdx
   */
  async toStep(stepIdx: number) {
    this.saveDataLayerDataToLocalStorage();
    if (stepIdx === OrderCheckoutStepEnum.PUBLIC_TRANSPORT && this.aoicsToBeShownForPubTrans.length === 0) {
      if (this.currentIndex === OrderCheckoutStepEnum.PAYMENT) {
        stepIdx--;
      } else {
        stepIdx++;
      }
    }
    if (this.$refs.formWizard) {
      this.goToStep = stepIdx;
      let index = this.programmaticIndex;

      // if the step we want to jump to is higher than the current tab
      if (stepIdx > index) {
        if (await this.isStepValid(index)) {
          // if it's more than one step we need to jump
          if ((stepIdx - index) > 1) {
            this.isAwaitingNextTab = true;
            // @ts-ignore
            this.$refs.formWizard.nextTab();
          } else {
            // if the review tab is passed we want to save data in local storage to send it to DataLayer
            if (this.currentIndex === this.reviewStep) {
              this.saveDataLayerDataToLocalStorage();
            }

            // we want to jump only to the next tab

            this.programmaticIndex++;
            this.isAwaitingNextTab = false;
            this.goToStep = null;
            // @ts-ignore
            this.$refs.formWizard.nextTab();
          }
        } else {
          this.isAwaitingNextTab = false;
          this.goToStep = null;
          this.loading = false;
        }

      } else if (stepIdx < index) {
        // jump to any previous tab
        this.programmaticIndex = stepIdx;
        this.isAwaitingNextTab = false;
        this.goToStep = null;
        // @ts-ignore
        this.$refs.formWizard.changeTab(index, stepIdx);
      }
    }
  }

  handleClick(stepIdx) {
    this.pageRefreshed = false;
    this.toStep(stepIdx);
    this.validateSurvey = true;
  }

  async isStepValid(step: number) {
    switch (step) {
      case OrderCheckoutStepEnum.PERSONALIZATION:
        return this.isMounted && this.validatePersonalizationForm() && this.validateSurveys();
      case OrderCheckoutStepEnum.PUBLIC_TRANSPORT:
        return this.isMounted && await this.validatePubTrans();
      case OrderCheckoutStepEnum.PAYMENT:
        return this.isMounted && await this.validatePayment();
      case OrderCheckoutStepEnum.REVIEW:
        return this.isMounted && this.checkoutFormIsValid;
      default:
        return this.isMounted;
    }
  }

  getInitialSurveyData(event: Event) {
    const key = this.getEventResultKey(event.id);
    if (this.personalizationDTO.surveys && this.personalizationDTO.surveys[key]) {
      return this.personalizationDTO.surveys[key];
    }
    return null;
  }

  updateSurveyData(data: VisitorSurveyResultDTO) {
    this.personalizationDTO.surveys[this.getEventResultKey(data.eventId)] = data;
  }

  getEventResultKey(id: number | string) {
    return `event_${id}`;
  }

  initPaymentInfoDTO(paymentProduct: string) {
    if (this.paymentDTOMapping[paymentProduct] && this.cart!.sum > 0) {
      this.paymentInfoDTO = new this.paymentDTOMapping[paymentProduct]();
    } else {
      this.paymentInfoDTO = null;
    }
  }

  @Watch('personalizationDTO.paymentOption')
  onPaymentOptionChange(newVal: string, oldVal: string) {
    this.initPaymentInfoDTO(newVal);
    if (this.applePayService.isApplePayAvailable() && this.cart && newVal === PaymentTypeEnum.applepay.value) {
      this.applePaySession = this.applePayService.createSession(this.cart.sum, 'DE');
      this.initApplePaySession();
      this.applePayService.beginSession(this.applePaySession);
    }
  }

  updatePageRefreshedAndCallToStepFunction(index) {
    this.pageRefreshed = false;
    this.toStep(index);
  }

  @Watch('currentIndex')
  async onCheckoutStepChange(newVal: number, oldVal: number) {
    if (!this.pageRefreshed) {
      if (newVal === 0 && this.aoicsToBeShownForPubTrans.length > 0) {
        // @ts-ignore
        window.dataLayer.push({
          event: 'checkout_personalisation',
        });
        sessionStorage.setItem('sentDataLayerPayment', '1');
      } else if (newVal === 1 && this.aoicsToBeShownForPubTrans.length > 0) {
        // @ts-ignore
        window.dataLayer.push({
          event: 'checkout_localTransport',
        });
        sessionStorage.setItem('sentDataLayerPayment', '1');
      } else if (newVal === 2) {
        // @ts-ignore
        window.dataLayer.push({
          event: 'checkout_payment',
        });
        sessionStorage.setItem('sentDataLayerPayment', '1');
      } else if (newVal === 3) {
        // @ts-ignore
        window.dataLayer.push({
          event: 'checkout_overview',
        });
        sessionStorage.setItem('sentDataLayerPayment', '1');
      }
    }
    // if on (last) check details step
    if (newVal === 3) {
      // fetch DPC & TAC & AN of different clients
      const clientIds = this.clients.map(c => c.id);
      const statements = await this.privacyStatementService.listCached<PrivacyStatement>('', {clientId: `[${clientIds.join(',')}]`});
      const termsAndConditions = await this.termsAndConditionsService.listCached<TermsAndConditions>(
        '',
        {clientId: `[${clientIds.join(',')}]`},
      );
      const additionalNotices = await this.additionalNoticeService.listCached<AdditionalNotice>('', {clientId: `[${clientIds.join(',')}]`});
      this.clients.forEach(client => {
        const ps = statements.find(p => p.clientId === client.id);
        const tac = termsAndConditions.find(p => p.clientId === client.id);
        const an = additionalNotices.find(p => p.clientId === client.id);
        if (ps) {
          Vue.set(this.privacyStatements, `${client.id}`, ps);
        }
        if (tac) {
          Vue.set(this.agb, `${client.id}`, tac);
        }
        if (an) {
          Vue.set(this.additionalNotices, `${client.id}`, an);
        }
      });

      // Fetch paypal-button on last step if chosen as payment
      if (this.currentOrder?.buyerData?.paymentOption === PaymentTypeEnum.paypal.value) {
        await this.fetchPayPalSdkJs();
      }
    }
  }

  initApplePaySession() {
    if (this.applePaySession) {
      this.applePaySession.oncancel = (e) => {
        if (this.applePaySession) {
          this.applePayService.cancelSession(this.applePaySession);
        }
        this.applePaySession = null;
      };

      this.applePaySession.onpaymentauthorized = (e) => {
        if (this.applePaySession) {
          this.applePayService.completePayment(this.applePaySession);
          (this.paymentInfoDTO as ApplePayDTO).applePayToken = e.payment.token;
        }
      };

      this.applePaySession.onpaymentmethodselected = (e) => {
        if (this.applePaySession && this.cart) {
          this.applePayService.completePaymentMethodSelection(this.applePaySession, e, this.cart.sum);
        }
      };

      this.applePaySession.onshippingmethodselected = (e) => {
        if (this.applePaySession && this.cart) {
          this.applePayService.completeShippingMethodSelection(this.applePaySession, e, this.cart.sum);
        }
      };

      this.applePaySession.onshippingcontactselected = (e) => {
        if (this.applePaySession && this.cart) {
          this.applePayService.completeShippingContactSelection(this.applePaySession, e, this.cart.sum);
        }
      };

      this.applePaySession.onvalidatemerchant = async (e) => {
        if (this.applePaySession) {
          const merchantSession = await this.applePayService.fetchMerchantSession(e.validationURL);
          this.applePayService.completeMerchantValidation(
            this.applePaySession,
            merchantSession,
          );
        }
      };
    }
  }

  /**
   *  replaces the current URL with an updated one containing the new step index.
   *  Allows to reload the page and directly continue where left.
   *  Also required for possible redirect URLs e.g. for concardis.
   */
  updateWindowUrl() {
    if (!this.isMounted) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('step', `${this.currentIndex}`);
    const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
    history.replaceState(null, document.title, newRelativePathQuery);
  }

  async onPersonalUseChange() {
    if (this.hasSelectedForPersonalUse) {
      await this.toStep(OrderCheckoutStepEnum.PERSONALIZATION);
    } else {
      await this.toStep(this.determineSecondStep);
    }
  }

  async submitPublicTransport() {
    if (this.personalizationDTO.publicTransportConfigs.length === 0) {
      this.personalizationDTO.usePublicTransport = false;
    }

    await this.validatePersonalization();
  }

  getStepFromUrl(): number | null {
    const paramsString = window.location.search.replace('?', '');
    const params = new URLSearchParams(paramsString);
    const rawStepVal = params.get('step');
    if (rawStepVal !== null) {
      let step = parseInt(rawStepVal, 10);
      if (step >= 3) {
        step = 2;
      }
      return step;
    }
    return null;
  }

  saveDataLayerDataToLocalStorage() {
    if (!this.cart || !this.cart.order) {
      return;
    }

    const items: object[] = [];
    let cartPromotionCodes: string;
    const transactionId = this.cart.order.orderNumberCompiled || '';
    const totalValue = this.cart.sum.toFixed(2) || '';
    const totalVat = this.cart.vatSum.toFixed(2) || '';
    const paymentOption = this.cart.order.buyerData?.paymentOption || '';
    let currency: string = '';

    // @ts-ignore
    if (this.cart.cartPromotions.length >= 1) {
      const cartPromotionCodesArray: string[] = [];
      this.cart.cartPromotions.forEach((item) => {
        cartPromotionCodesArray.push(item.code);
      });
      cartPromotionCodes = cartPromotionCodesArray.join(', ');
    } else {
      cartPromotionCodes = '';
    }

    this.products.forEach((item) => {
      const event = this.getEvent(item.eventId);
      let client: Client | null = null;
      if (event) {
        client = this.getClient(event.clientId);
      }

      const ticketName = item.descriptionExternal;
      const ticketId = String(item.id);
      const ticketPrice = item.price.toFixed(2);
      const item_variant = {var1: '', var2: '', var3: ''};
      let brand: string = event ? event.descriptionExternal : '';
      let itemCoupon: string = '';
      let itemDiscount: string = '';
      let itemQuantity: number = 0;
      let organizer: string = client ? client.title : '';
      item_variant.var3 = item.localPubTransIncluded === 1 ? 'Ja' : 'Nein';

      this.cart!.advertised?.forEach((e) => {
        if (e.productId === item.id) {
          item_variant.var1 = e.code;
          const ev = this.getEvent(item.eventId);
          if (ev) {
            brand = ev.descriptionExternal;
            const cl = this.getClient(ev.clientId);
            if (cl) {
              organizer = cl.title;
              item_variant.var2 = organizer;
            }
          }
        }
      });

      this.cart!.promotedProducts?.forEach((e) => {
        if (e.productId === item.id) {
          itemCoupon = e.code;
          itemDiscount = e.discountValue.toFixed(2);
        }
      });

      this.cart!.order.orderItems?.forEach((e) => {
        if (e.productId === item.id) {
          itemQuantity = e.quantity;
        }
      });

      currency = item.currency === '€' ? 'EUR' : item.currency;

      items.push({
        item_name: ticketName,
        item_id: ticketId,
        price: ticketPrice,
        currency: currency,
        item_brand: brand,
        item_coupon: itemCoupon,
        discount: itemDiscount,
        item_category: organizer,
        item_category2: '',
        item_category3: '',
        item_category4: '',
        item_variant: item_variant,
        quantity: itemQuantity,
      });
    });

    if (this.cart.promotedProducts) {
      const productPromotionPositions: {
        [key: string]: {
          name: string;
          id: 'Gutscheine';
          price: string;
          brand: string;
          category: string;
          quantity: number;
        },
      } = {};
      this.cart.promotedProducts.forEach((product) => {
        if (!productPromotionPositions[product.code]) {
          const event = this.getEvent(product.eventId);
          let codeItemBrand = event ? event.descriptionExternal : '';
          let codeItemCategory = '';
          if (event) {
            const client = this.getClient(event.clientId);
            if (client) {
              codeItemCategory = client.title;
            }
          }
          productPromotionPositions[product.code] = {
            name: product.code,
            id: 'Gutscheine',
            price: `-${product.discountValue.toFixed(2)}`,
            brand: codeItemBrand,
            category: codeItemCategory,
            quantity: 0,
          };
        }
        productPromotionPositions[product.code].quantity += 1;
        const newPrice = product.discountValue * productPromotionPositions[product.code].quantity;
        productPromotionPositions[product.code].price = `-${(newPrice).toFixed(2)}`;
      });

      Object.entries(productPromotionPositions).forEach(([key, value]) => {
        items.push({
          item_name: value.name,
          item_id: value.id,
          price: value.price,
          currency: 'EUR',
          item_brand: value.brand,
          item_coupon: '',
          discount: '',
          item_category: value.category,
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_variant: {var1: '', var2: '', var3: ''},
          quantity: value.quantity,
        });
      });
    }

    if (this.cart!.cartPromotions && this.cart!.cartPromotions.length) {
      this.cart!.cartPromotions.forEach((cartPromotion) => {
        const sums: CartPromotionSumEntry[] = [];
        let eventTitle = '';
        let clientTitle = '';
        cartPromotion.applyToConfig.forEach(c => {
          const cartDiscountSum = this.cart!.cartPromotionSums[c.eventId];
          if (cartDiscountSum) {
            sums.push(cartDiscountSum);
          }
          const event = this.events.find(e => e.id === c.eventId);
          if (event) {
            eventTitle = event.descriptionExternal;
            const client = this.clients.find(cl => cl.id === event.clientId);
            if (client) {
              clientTitle = client.title;
            }
          }
        });

        const total = sums
          .map(s => s.amount)
          .map(a => Number.parseInt(a, 10))
          .reduce((sumVal, curr) => sumVal + curr, 0) / 100;

        items.push({
          item_name: cartPromotion.code,
          item_id: 'Gutscheine',
          price: `-${total.toFixed(2)}`,
          currency: 'EUR',
          item_brand: eventTitle,
          item_coupon: '',
          discount: '',
          item_category: clientTitle,
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_variant: {var1: '', var2: '', var3: ''},
          quantity: 1,
        });
      });
    }

    const dataLayerData = {
      event: 'purchase',
      transaction_id: transactionId,
      value: totalValue,
      tax: totalVat,
      currency: currency,
      coupon: cartPromotionCodes,
      paymentConditions: paymentOption,
      deliveryConditions: '',
      items,
    };

    localStorage.setItem('dataLayerData', JSON.stringify(dataLayerData));
  }

  openModal() {
    // open Modal to select public transportation date
    const element = this.$refs.pubTransComponent;
    if (element) {
      // @ts-ignore
      element.openModal();
    }
  }

  openPrivacyModal(client: Client) {
    this.currentClient = `${client.id}`;
    // @ts-ignore
    this.$refs.privacyModal.showModal();
  }

  openAGBModal(client: Client) {
    this.currentClient = `${client.id}`;
    // @ts-ignore
    this.$refs.agbModal.showModal();
  }

  async updateCheckoutData(): Promise<boolean> {
    try {
      this.checkoutDataDTO.invoiceData = (new CustomerDataDTO()).fromData(this.invoiceDataDTO);
      this.checkoutDataDTO.buyerData = (new CustomerDataDTO()).fromData(this.personalizationDTO);
      this.checkoutDataDTO.additionalOrderItemConfigurations = [];
      this.cart?.order.additionalOrderItemConfigurations.forEach((aoic) => {
        this.checkoutDataDTO.additionalOrderItemConfigurations.push(
          (new AdditionalOrderItemConfigurationDTO()).fromModel(aoic),
        );
      });
      if (
        this.paymentInfoDTO
        && this.cart
        && this.personalizationDTO.paymentOption
        && Object.values(this.paymentInfoDTO).filter(e => !!e).length > 0
        && this.cart.sum > 0
      ) {
        this.checkoutDataDTO.paymentInfo = (new this.paymentInfoDTOClass()).fromData(this.paymentInfoDTO);
      } else {
        this.checkoutDataDTO.paymentInfo = null;
      }
      this.shopUrlsDTO.successUrl = `${this.successUrl}#${this.cart?.order.orderNumberCompiled}` || null;
      this.shopUrlsDTO.failureUrl = `${this.failureUrl}#${this.cart?.order.orderNumberCompiled}` || null;
      this.shopUrlsDTO.cancelUrl = `${this.cancelUrl}#${this.cart?.order.orderNumberCompiled}` || null;
      this.checkoutDataDTO.shopUrls = (new ShopUrlsDTO()).fromData(this.shopUrlsDTO);
      const cart = await this.orderService.updateCheckoutData(this.checkoutDataDTO, this.userLanguage);
      this.$store.commit(UPDATE_ORDER, cart.order);
      this.$store.commit(SET_BUYER_DATA, cart.order.buyerData);
      return true;
    } catch (e) {
      // TODO: sentry error
      this.handleApiError(e);
      return false;
    }
  }

  handleApiError(e) {
    console.error(e);
    // TODO: sentry error
    const errorHandler = new ApiErrorHandler();
    errorHandler.handle(e);
    if (errorHandler.getErrorCode(e) === OrderErrorCodes.ORDER_PRODUCT_QUANTITY_INVALID) {
      const data = errorHandler.getErrorData(e);
      if (data) {
        this.quantityErrorData = data;
        this.showQuantityError = true;
      }
    }
    if (errorHandler.getErrorCode(e) === OrderErrorCodes.ORDER_SUBMITTED) {
      this.postCheckoutSuccess();
    }
  }

  hasNameAndSurname(name: string): boolean {
    return name.trim().split(' ').length > 1;
  }

  isPublicTransportValid() {
    let invalidPubTransConfigs: Array<AdditionalOrderItemConfiguration> = [];
    const additionalOrderItemConfigs = this.cart?.order.additionalOrderItemConfigurations;
    if (additionalOrderItemConfigs) {
      additionalOrderItemConfigs.forEach((aoic) => {
        const ptc = aoic.publicTransportConfig;
        if (ptc) {
          if (ptc.usePubTrans &&
            (!ptc.passengerName || !ptc.passengerSurname
              || ptc.passengerName === '' || ptc.passengerSurname === ''
              || (!ptc.selectedDate && !this.productIsMultipleDays(aoic))
            )) {
            invalidPubTransConfigs.push(aoic);
          }
        }
      });
      return invalidPubTransConfigs.length === 0;

    }
    return false;
  }

  unsetPubTransConfigs() {
    const additionalOrderItemConfigs = this.cart?.order.additionalOrderItemConfigurations;
    if (additionalOrderItemConfigs) {
      additionalOrderItemConfigs.forEach((aoic) => {
        if (aoic.publicTransportConfig) {
          aoic.publicTransportConfig.usePubTrans = false;
        }
      });
    }
  }

  productIsMultipleDays(aoic: AdditionalOrderItemConfiguration): boolean {
    return this.productAdmissionDays(aoic) > 1 || this.isAussteller;
  }

  productAdmissionDays(aoic: AdditionalOrderItemConfiguration): number {
    return aoic.getProduct()?.numberFirstDayAdmissions || 1;
  }

  getEvent(id: string | number): Event | null {
    return this.events.find(e => e.id === id) || null;
  }

  getClient(id: string | number): Client | null {
    return this.clients.find(c => c.id === id) || null;
  }

  get activeVisitorSegments() {
    const segmentIds = this.products.map(p => p.visitorSegmentId);
    return this.visitorSegments.filter(vs => segmentIds.includes(vs.id as number));
  }

  get isAbbau() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.ABBAU) !== undefined;
  }

  get isAufbau() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.AUFBAU) !== undefined;
  }

  get isAussteller() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.AUSSTELLER) !== undefined;
  }

  get isBesucher() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.BESUCHER) !== undefined;
  }

  get isPresse() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.PRESSE) !== undefined;
  }

  get isVIP() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.VIP) !== undefined;
  }

  get determineSecondStep(): number {
    if (this.aoicsToBeShownForPubTrans.length > 0) {
      return this.pubTransStep;
    }

    return this.paymentStep;
  }

  get paymentStep(): number {
    return OrderCheckoutStepEnum.PAYMENT;
  }

  get pubTransStep(): number {
    return OrderCheckoutStepEnum.PUBLIC_TRANSPORT;
  }

  get personalizationStep(): number {
    return OrderCheckoutStepEnum.PERSONALIZATION;
  }

  get reviewStep(): number {
    return OrderCheckoutStepEnum.REVIEW;
  }

  get paymentDTOMapping() {
    return {
      creditcard: CreditCardDTO,
      // 'klarna-pay-now': KlarnaDTO,
      // 'ratepay-invoice': null,
      paypal: PayPalDTO,
      applepay: ApplePayDTO,
      sepa: SepaDTO,
    };
  }

  get paymentInfoForm(): string | null {
    if (this.personalizationDTO.paymentOption) {
      const paymentInfoData = PaymentInfoFormEnum[this.personalizationDTO.paymentOption];
      return paymentInfoData || null;
    }
    return null;
  }

  get paymentInfoDTOClass() {
    if (this.personalizationDTO.paymentOption) {
      return this.paymentDTOMapping[this.personalizationDTO.paymentOption];
    }
    return null;
  }

  get invalidProductQuantityProduct(): any | null {
    if (this.quantityErrorData) {
      const pId = this.quantityErrorData.invalidProducts[0].product.id;
      const product = this.products.find(p => p.id === pId);
      return product ? product : null;
    }
    return null;
  }

  get invalidProductQuantityType(): number | null {
    if (this.quantityErrorData) {
      return Number.parseInt(this.quantityErrorData.invalidProducts[0].error, 10);
    }
    return null;
  }

  get isQuantityInvalidTooMany() {
    return this.invalidProductQuantityType === ProductQuantityErrorEnum.ERROR_CODE_MAX_LIMIT_EXCEEDED;
  }

  get isQuantityInvalidTooFew() {
    return this.invalidProductQuantityType === ProductQuantityErrorEnum.ERROR_CODE_MIN_LIMIT_NOT_REACHED;
  }

  get hasSurveys(): boolean {
    const surveys = this.events.map(e => e.visitorSurvey);
    return surveys.filter(s => s !== null).length > 0;
  }

  get addressTypeChoices() {
    return [
      {value: AddressTypeEnum.NONE.value, text: AddressTypeEnum.NONE.title[this.userLanguage]},
      {value: AddressTypeEnum.PRIVATE.value, text: AddressTypeEnum.PRIVATE.title[this.userLanguage]},
      {value: AddressTypeEnum.COMMERCIAL_BUSINESS.value, text: AddressTypeEnum.COMMERCIAL_BUSINESS.title[this.userLanguage]},
    ];
  }

  get paymentChoices() {
    const choices: Array<{ value: string, text: string, disabled: boolean }> = [];
    this.paymentProducts.forEach((p) => {
      if (PaymentTypeEnum[p]) {
        choices.push({
          value: PaymentTypeEnum[p].value,
          text: PaymentTypeEnum[p].title[this.userLanguage],
          disabled: !(
            (PaymentTypeEnum[p].value === PaymentTypeEnum.applepay.value && this.applePayService.isApplePayAvailable())
            || PaymentTypeEnum[p].value !== PaymentTypeEnum.applepay.value
          ),
        });
      }
    });
    return choices;
  }

  get checkoutUrl() {
    return window.location.href.split('?')[0];
  }

  get selectedPrivacyStatement() {
    if (this.currentClient) {
      return this.privacyStatements[this.currentClient] || null;
    }
    return null;
  }

  get selectedAGB() {
    if (this.currentClient) {
      return this.agb[this.currentClient] || null;
    }
    return null;
  }

  get i18nTermsAndConditionsLink(): string {
    return PublicLinks.TERMS_AND_CONDITIONS[this.userLanguage];
  }

  get i18nPrivacyStatementLink(): string {
    return PublicLinks.PRIVACY_STATEMENT[this.userLanguage];
  }

  get i18nPrivacyStatementContent(): string {
    if (this.selectedPrivacyStatement) {
      let content = this.selectedPrivacyStatement.html;
      if (this.userLanguage !== 'de' && this.selectedPrivacyStatement.translations) {
        const translation = this.selectedPrivacyStatement.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  get i18nTermsAndConditionsContent(): string {
    if (this.selectedAGB) {
      let content = this.selectedAGB.html;
      if (this.userLanguage !== 'de' && this.selectedAGB.translations) {
        const translation = this.selectedAGB.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  get anyProductHasPubTransIncluded() {
    return this.usablePubTransProducts.filter(p => p.localPubTransIncluded === 1).length > 0 || true;
  }

  get aoicsToBeShownForPubTrans(): Array<AdditionalOrderItemConfiguration> {
    let aoicsToBeShownForPubTrans: Array<AdditionalOrderItemConfiguration> = [];
    let additionalProductsCount = 0;

    if (this.cart?.order) {
      this.cart.order.additionalOrderItemConfigurations.forEach((aoic) => {
        // check if pubTrans is set for this ticket
        if (aoic.publicTransportConfig) {
          const product = aoic.getProduct();
          // If ticket has no registrationReq, add it
          if (!product?.isRegistrationRequired) {
            aoicsToBeShownForPubTrans.push(aoic);
          }
          // If ticket has registrationReq and is selected for personal use, add it
          if (product?.isRegistrationRequired && aoic.personalUse) {
            aoicsToBeShownForPubTrans.push(aoic);
          }
          // Count the amount of additionalProducts added here
          if (product?.additionalProduct) {
            additionalProductsCount++;
          }
        }
      });
    }
    if (additionalProductsCount === aoicsToBeShownForPubTrans.length) {
      // Only additionalProducts are in the toBeShownAoics.
      // As those can't be selected for publicTransport themselves, return an empty array
      return [];
    }
    return aoicsToBeShownForPubTrans;
  }

  /**
   * Only products without registration required, or personal use selected
   */
  get usablePubTransProducts() {
    if (this.currentOrder) {
      const usableOrderItems = this.currentOrder.orderItems.filter((oi) => {
        return (oi.isRegistrationRequired && oi.personalUse) || !oi.isRegistrationRequired;
      });
      const productIds = [...new Set(usableOrderItems.map(oi => oi.productId))];
      return this.products.filter(p => productIds.includes(<number>p.id));
    }
    return [];
  }

  get hasSelectedPubTrans(): boolean {
    return this.personalizationDTO.publicTransportConfigs.filter((pubT => pubT.usePubTrans)).length > 0;
  }

  get hasSelectedForPersonalUse(): boolean {
    let areItemsSelected = false;
    if (this.cart) {
      for (const orderItemConfig of this.cart.order.additionalOrderItemConfigurations) {
        if (orderItemConfig.personalUse) {
          areItemsSelected = true;
          break;
        }
      }
    }
    return areItemsSelected;
  }

  get hasItemsWithRegistrationRequired(): boolean {
    if (this.cart) {
      for (const orderItem of this.cart.order.orderItems) {
        if (orderItem.registrationRequired === 'Ja') {
          return true;
        }
      }
    }
    return false;
  }

  get checkoutFormIsValid() {
    return this.validatePaymentForm()
      && this.validateSurveys()
      && this.validatePersonalizationForm()
      && this.isPublicTransportValid()
      && (
        (this.clientsWithConsentExist && this.acceptedClientsAGB && this.acceptedClientsPrivacyStatements)
        || (this.personalizationDTO.fallbackPrivacyChecked && this.personalizationDTO.fallbackAgbChecked)
      );
  }

  get acceptedClientsAGB() {
    const clientsWithAGB = Object.keys(this.agb).length;
    return clientsWithAGB > 0 ?
      this.personalizationDTO.termsAndConditions.length === clientsWithAGB :
      this.personalizationDTO.fallbackAgbChecked;
  }

  get acceptedClientsPrivacyStatements() {
    const clientsWithPrivacyStatements = Object.keys(this.privacyStatements).length;
    return clientsWithPrivacyStatements > 0 ?
      this.personalizationDTO.privacyStatements.length === clientsWithPrivacyStatements :
      this.personalizationDTO.fallbackPrivacyChecked;
  }

  get clientsWithConsent() {
    const ids = Object.keys(this.privacyStatements);
    const agbIds = Object.keys(this.agb);
    return this.clients.filter(c => ids.indexOf(`${c.id}`) >= 0 || agbIds.indexOf(`${c.id}`) >= 0);
  }

  eventsWithConsent(clientId: number) {
    return this.events.filter(e => clientId === e.clientId);
  }

  eventConsentString(clientId: number): string {
    const titles = this.eventsWithConsent(clientId).map(e => e.descriptionExternal);
    if (titles.length) {
      return titles.join(', ');
    }
    return '';
  }

  get clientsWithConsentExist() {
    return this.clientsWithConsent.length > 0;
  }

  get isCurrentStepValid() {
    switch (this.currentIndex) {
      case OrderCheckoutStepEnum.PERSONALIZATION:
        return this.isMounted && this.validatePersonalizationForm() && this.validateSurveys();
      case OrderCheckoutStepEnum.PUBLIC_TRANSPORT:
        return this.isMounted && this.anyProductHasPubTransIncluded && this.isPublicTransportValid();
      case OrderCheckoutStepEnum.PAYMENT:
        return this.isMounted && this.validatePaymentForm();
      case OrderCheckoutStepEnum.REVIEW:
        return this.isMounted && this.checkoutFormIsValid;
      default:
        return this.isMounted;
    }
  }

  get postCheckout() {
    return (!this.currentOrder || !this.cart) && !this.isRequesting;
  }

  get isInactiveAndRefreshTimer() {
    if (this.postCheckout) {
      if (!this.inactivityTimer) {
        document.addEventListener('click', this.refreshInactivityTimer);
        this.refreshInactivityTimer();
      }
    }
    return this.isInactive;
  }

  get shouldGenerateDownloadLinks() {
    return !!this.events.find((e) => e.provideDownloadLink);
  }

  additionalNoticeHtmlFor(clientId: number | string): string {
    const notice = this.additionalNotices.find(x => x !== undefined && x.clientId === parseInt(clientId.toString(), 10));
    if (notice) {
      if (this.userLanguage !== DEFAULT_LANGUAGE) {
        const translations = notice.translations;
        if (translations && this.userLanguage in translations) {
          return translations[this.userLanguage].html || '';
        }
      }
      return notice?.html || '';
    }
    return '';
  }

  get fallbackAdditionalNoticeHtml(): string {
    const notice = this.additionalNotices.find(x => x !== undefined);
    if (notice) {
      if (this.userLanguage !== DEFAULT_LANGUAGE) {
        const translations = notice.translations;
        if (translations && this.userLanguage in translations) {
          return translations[this.userLanguage].html || '';
        }
      }
      return notice?.html || '';
    }
    return '';
  }

  /**
   * sets an intersection from restricted payment-methods (from all events currently in basket) and
   * all active payment-methods
   */
  setAvailablePaymentMethods() {
    // Get only ids-array from active payment-methods
    let activePaymentMethodIds = this.paymentMethods.map((pm, key) => pm.id);
    // If paymentMethods are empty for the event, all paymentmethods are allowed, so set the active paymentmethods
    let events = this.events;
    events.forEach(function (event) {
      if (!event.paymentMethods || event.paymentMethods.length === 0) {
        // @ts-ignore
        event.paymentMethods = activePaymentMethodIds;
      }
    });

    // Create an intersection for N sets of payment-method-ids
    let intersectedEventPaymentMethodsIds = events[0].paymentMethods;
    events.forEach(function (event) {
      intersectedEventPaymentMethodsIds = event.paymentMethods.filter(value => intersectedEventPaymentMethodsIds.includes(value));
    });

    // Create intersection from event-paymentmethods and active paymentmethods
    // Just in case an inactive paymentmethod is set for an event
    const intersection = intersectedEventPaymentMethodsIds.filter(value => activePaymentMethodIds.includes(value));

    // Return payment-methods form intersectionId-Array
    // @ts-ignore
    let availablePaymentMethods = this.paymentMethods.filter(pm => intersection.indexOf(pm.id) !== -1);

    this.paymentProducts = [];
    if (availablePaymentMethods.length > 0) {
      // Display all paymentMethods that are active and set available in events (lowest common denominator)
      availablePaymentMethods.forEach((pm) => {
        this.paymentProducts.push(pm.type);
        this.initPaymentInfoDTO(pm.type);
      });
    } else {
      // Fallback to credit-card if there are no available payment methods
      this.paymentProducts = [PaymentTypeEnum.creditcard.value];
      this.personalizationDTO.paymentOption = PaymentTypeEnum.creditcard.value;
      this.initPaymentInfoDTO(PaymentTypeEnum.creditcard.value);
    }
  }

  refreshInactivityTimer() {
    this.isInactive = false;
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(
      () => {
        this.isInactive = true;
      },
      15 * 60 * 1000,
    );
  }

  async logout() {
    let success = true;
    if (this.sessionDTO && this.sessionDTO.session) {
      success = await this.authService.logout(this.sessionDTO.session);
    }

    if (success) {
      this.$store.commit(CLEAR_IS_GUEST_ORDER);
      this.$store.commit(CLEAR_USER);
      this.$store.commit(CLEAR_BUYER_DATA);
      this.$store.commit(CLEAR_SESSION);
    }
  }
}
